import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import {PlanDetailChartV2Component} from "../plan-detail-chart-v2/plan-detail-chart-v2.component";
import {
  trigger,
  state,
  style,
  animate,
  transition, group,
  // ...
} from "@angular/animations";
import {Features} from '../../../models/plan';
import {AuthenticationService} from '../../../authentication.service';

let animations = [
  trigger("paramsAnimation", [
    transition(":enter", [
      style({ transform: "translateX(100%)",opacity:1}),
      group([animate("250ms ease-out", style({ transform: "!" ,opacity:"!"}))]),
    ]),
    transition(":leave", [
      style({ transform: "!",opacity:"!"}),
      group([animate("250ms ease-out", style({ transform: "translateX(100%)",opacity:0 }))]),
    ])
  ])
];


@Component({
  standalone: false,
  selector: "plan-multi-chart",
  templateUrl: "./plan-multi-chart.component.html",
  styleUrls: ["./plan-multi-chart.component.scss"],
  animations
})
export class PlanMultiChartComponent implements OnInit,OnChanges,AfterViewInit {

  @ViewChild("chart0") chart0: PlanDetailChartV2Component;
  @ViewChild("chart1") chart1: PlanDetailChartV2Component;
  @ViewChild("chart2") chart2: PlanDetailChartV2Component;
  @ViewChild("chart3") chart3: PlanDetailChartV2Component;

  @Input() gridContext;
  @Output() drillDown: EventEmitter<any> = new EventEmitter<any>();
  @Output() rollUp: EventEmitter<any> = new EventEmitter<any>();
  @Input() rowData;
  @Input() isLoading;
  @Input() columnDefs;
  @Input() isVisible = false;
  @Input() viewMode;
  @Input() contactType;
  @Input() chartsCount=4;
  @Input() locale = "en-US";


  currentParamsList = 0;
  paramsList = []
  charts=[];
  sidebar=["Hidden","Hidden","Hidden","Hidden"];

  isSidebarOpen:boolean;
  refreshTimer = null;

  // Element that had focus when the Parameters panel was opened, so focus can be
  // returned there when the panel closes (WCAG 2.4.3 Focus Order).
  private _paramsTrigger: HTMLElement | null = null;

  constructor(private authService: AuthenticationService, private elementRef: ElementRef) {

  }

  ngOnInit(): void {

  }
  ngAfterViewInit() {
    this.charts=[this.chart0,this.chart1,this.chart2,this.chart3];
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isVisible"] && changes["isVisible"].currentValue === false) {
      this.sidebar = Array(4).fill("Hidden");
      this.isSidebarOpen = false;
    }
    if(changes["chartsCount"]){
      this.onCloseParams();
    }
    if(changes["isLoading"]){
      this.onCloseParams();
    }
  }
  onParamsListChanged(params,chartId){
    this.paramsList[chartId]=params;
  }

  onParamSelectionChange(item){
    this.charts[this.currentParamsList].onParamSelectionChange(item);
  }

  updateViewMode(e){
    if(e===false){
      this.drillDown.emit();
    }else{
      this.rollUp.emit();
    }
  }
  onDisplayParams(id){
    // Remember the control that opened the panel so focus can return to it on
    // close (WCAG 2.4.3).
    this._paramsTrigger = document.activeElement as HTMLElement;
    this.currentParamsList=id;
    this.isSidebarOpen=true;
    this.closeTooltips();
    // The Parameters/Y-AXIS panel is a modal flyout (dimmed overlay + disabled
    // charts behind it). Move keyboard focus into it once it has rendered so the
    // focus trap below has an entry point and Tab cycles WITHIN the panel instead
    // of escaping to the charts/toolbar underneath. (ESP-21743)
    setTimeout(() => {
      const focusable = this.getPanelFocusable();
      // preventScroll (ESP-21797): the grid/chart carousel this panel lives
      // inside (.row.slider in plan-detail-v5) is `overflow:hidden` with two
      // 100%-wide panels side by side, positioned via CSS transform rather
      // than by scrolling - but that still makes it a valid native scroll
      // container. Without preventScroll, focusing a control that the
      // browser considers off in the overflowing half sets scrollLeft on
      // that carousel, which stacks on top of its transform and is never
      // reset, permanently corrupting the chart/grid layout.
      focusable[0]?.focus({ preventScroll: true });
    });
  }

  onCloseParams(){
    // Capture whether the user was interacting inside the panel BEFORE it is
    // removed, so we only restore focus for a genuine (user) close and not for
    // programmatic closes (chart-count / loading changes) that would otherwise
    // yank focus away from wherever the user is.
    const panel = this.getParamsPanel();
    const focusWasInside = !!(panel && panel.contains(document.activeElement));
    this.currentParamsList=0;
    this.isSidebarOpen=false;
    if (focusWasInside && this._paramsTrigger && document.contains(this._paramsTrigger)) {
      // preventScroll (ESP-21797): see onDisplayParams.
      this._paramsTrigger.focus({ preventScroll: true });
    }
    this._paramsTrigger = null;
  }

  /** The Parameters/Y-AXIS flyout element, or null when it is not open. */
  private getParamsPanel(): HTMLElement | null {
    return (this.elementRef.nativeElement as HTMLElement).querySelector(".params-list-container");
  }

  /** Visible, focusable controls inside the Parameters panel, in tab order. */
  private getPanelFocusable(): HTMLElement[] {
    const panel = this.getParamsPanel();
    if (!panel) return [];
    return Array.from(
      panel.querySelectorAll(
        "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]"
      ) as NodeListOf<HTMLElement>
    ).filter((el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  // Focus trap: keep Tab / Shift+Tab inside the Parameters panel while it is open
  // so keyboard focus cannot move to the chart(s) and toolbar rendered behind the
  // modal flyout (WCAG 2.4.3 Focus Order / 2.1.2 No Keyboard Trap — the panel is
  // dismissible with Escape and the close button). (ESP-21743)
  @HostListener("keydown", ["$event"])
  trapFocus(event: KeyboardEvent): void {
    if (event.key !== "Tab" || !this.isSidebarOpen) return;
    const focusable = this.getPanelFocusable();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement;
    const panel = this.getParamsPanel();
    if (panel && !panel.contains(active)) {
      // Focus is outside the panel (e.g. still on the trigger button); pull it in.
      // preventScroll (ESP-21797): see onDisplayParams.
      event.preventDefault();
      first.focus({ preventScroll: true });
      return;
    }
    if (event.shiftKey) {
      if (active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  // Backstop for the focus trap: if focus still lands on an element behind the
  // panel (e.g. a mouse click or an assistive-technology gesture on the dimmed
  // chart), pull it back into the panel while the panel is open. (ESP-21743)
  @HostListener("document:focusin", ["$event"])
  onDocumentFocusIn(event: FocusEvent): void {
    if (!this.isSidebarOpen) return;
    const panel = this.getParamsPanel();
    if (!panel) return;
    if (!panel.contains(event.target as Node)) {
      // preventScroll (ESP-21797): see onDisplayParams.
      this.getPanelFocusable()[0]?.focus({ preventScroll: true });
    }
  }

  // Close the Parameters/Y-AXIS sidebar when Escape is pressed (WCAG 2.1.2 /
  // dialog dismiss expectation). Only acts while the sidebar is open so Escape
  // stays available to other consumers otherwise. Works regardless of whether
  // focus is on a parameter row, the close button, or elsewhere in the chart.
  // Guard against a modal (e.g. Plan Settings) that is open ABOVE the chart: an
  // NgbModal renders on top and owns Escape, so if one is present let it handle
  // the key — otherwise Escape meant for the modal would ALSO close the sidebar
  // underneath it and reset currentParamsList.
  @HostListener("document:keydown.escape", ["$event"])
  onEscapeKey(event: KeyboardEvent){
    if(this.isSidebarOpen && !document.querySelector("ngb-modal-window")){
      event.preventDefault();
      event.stopPropagation();
      this.onCloseParams();
    }
  }

  onPointHovered(source,$event: any) {
    this.charts.forEach((chart)=>{
      if(chart!=source){
        chart.highlight($event);
      }
    })

  }

  onPointOut(source, $event: any) {
    this.charts.forEach((chart)=>{
      if(chart!=source){
        chart.removeHighlight($event);
      }
    })
  }

  onSetExtremes($event: any) {

    this.chart0.syncExtremes($event);
    this.chart1.syncExtremes($event);
    this.chart2.syncExtremes($event);
    this.chart3.syncExtremes($event);
  }

  closeTooltips(){
    this.charts.forEach((chart)=>{
      chart.hideTooltips();
    })
  }

}
